import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import * as BackgroundFetch from 'expo-background-fetch';
import * as SecureStore from 'expo-secure-store';
import * as TaskManager from 'expo-task-manager';
import * as Notifications from 'expo-notifications';
import { fetch as expoFetch } from 'expo/fetch';

import type { SummaryPreferences, SummaryVariant } from '@/types/home-summary';
import { generateAPIUrl } from '@/utils';
import { NotificationPreferencesManager } from './NotificationPreferencesManager';
import { calendarSyncService } from './CalendarSyncService';
import { FamilyContextCollector } from '@/lib/family/FamilyContextCollector';
import { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';
import { ScopedAsyncStorage } from '@/lib/storage';

const IS_NATIVE = Platform.OS !== 'web';

const STORAGE_PREFS = 'fimfam-ai:summary:prefs';
// SecureStore on Android requires keys matching [A-Za-z0-9._-] — the older
// colon-separated AsyncStorage key isn't valid, so this is the canonical key.
const STORAGE_TOKEN = 'fimfam_ai_summary_token';
const STORAGE_USER_ID = 'fimfam-ai:summary:user-id';
const STORAGE_NOTIFICATIONS = 'fimfam-ai:summary:notification-ids';
const STORAGE_LAST_RUN = 'fimfam-ai:summary:last-run';
const BACKGROUND_TASK = 'fimfam-ai-home-summary-background-fetch';

// Trigger window: how many minutes before/after scheduled time to run
// Extended to 180 minutes (3 hours) for better reliability
const TRIGGER_WINDOW_MINUTES = 180;

type NotificationMap = Partial<Record<SummaryVariant, string>>;
type LastRunMap = Partial<Record<SummaryVariant, string>>;

type BackgroundWindow = {
  variant: SummaryVariant;
  targetDate: string;
  triggerAt: Date;
};

class SummaryScheduler {
  private isInitialized = false;
  private lastInitError: string | null = null;
  private initializationPromise: Promise<{ success: boolean; error?: string }> | null = null;

  async initialize(): Promise<{ success: boolean; error?: string }> {
    if (!IS_NATIVE) {
      this.isInitialized = true;
      console.log('[summaryScheduler] Skipping initialization: not on native platform');
      return { success: true };
    }

    if (this.isInitialized) {
      console.log('[summaryScheduler] Already initialized');
      return { success: true };
    }

    // Mutex: if initialization is already in progress, await the existing promise
    if (this.initializationPromise) {
      console.log('[summaryScheduler] Initialization already in progress, awaiting...');
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    try {
      return await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async performInitialization(): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[summaryScheduler] Starting initialization...');

      // Request notification permissions
      const notificationSettings = await Notifications.getPermissionsAsync();
      console.log('[summaryScheduler] Notification permissions:', notificationSettings.status);

      if (!notificationSettings.granted) {
        console.log('[summaryScheduler] Requesting notification permissions...');
        const result = await Notifications.requestPermissionsAsync();
        console.log('[summaryScheduler] Notification permissions result:', result.status);
      }

      // Check if task is already registered
      const isRegistered = await TaskManager.isTaskRegisteredAsync(BACKGROUND_TASK);
      console.log('[summaryScheduler] Task registration status:', isRegistered);

      if (!isRegistered) {
        console.log('[summaryScheduler] Registering background task...');
        await BackgroundFetch.registerTaskAsync(BACKGROUND_TASK, {
          minimumInterval: 15 * 60, // 15 minutes
          stopOnTerminate: false,
          startOnBoot: true,
        });
        console.log('[summaryScheduler] ✓ Background task registered successfully');
      } else {
        console.log('[summaryScheduler] Background task already registered');
      }

      // Verify registration with retry (native layer may need time to sync)
      const verifyRegistration = async (maxAttempts = 5, delayMs = 500): Promise<boolean> => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const isNowRegistered = await TaskManager.isTaskRegisteredAsync(BACKGROUND_TASK);
          if (isNowRegistered) {
            return true;
          }
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
        }
        return false;
      };

      const registrationVerified = await verifyRegistration();
      if (!registrationVerified) {
        // Non-blocking: log the error but don't throw, scheduler is a background enhancement
        console.warn('[summaryScheduler] Task registration verification failed after retries — continuing without background task');
      }

      // Check background fetch status
      const bgStatus = await BackgroundFetch.getStatusAsync();
      const statusMap: Record<number, string> = {
        [BackgroundFetch.BackgroundFetchStatus.Restricted]: 'Restricted',
        [BackgroundFetch.BackgroundFetchStatus.Denied]: 'Denied',
        [BackgroundFetch.BackgroundFetchStatus.Available]: 'Available',
      };
      console.log('[summaryScheduler] Background fetch status:', (bgStatus !== null && statusMap[bgStatus]) || bgStatus);

      this.isInitialized = true;
      this.lastInitError = null;
      console.log('[summaryScheduler] ✓ Initialization complete');
      return { success: true };
    } catch (error) {
      const errorMessage = (error as Error).message;
      this.lastInitError = errorMessage;
      console.error('[summaryScheduler] ✗ Initialize failed:', error);
      return { success: false, error: errorMessage };
    }
  }

  getLastInitError(): string | null {
    return this.lastInitError;
  }

  async forceReregister(): Promise<{ success: boolean; error?: string }> {
    console.log('[summaryScheduler] Force re-registering background task...');

    try {
      // Unregister if already registered
      const isRegistered = await TaskManager.isTaskRegisteredAsync(BACKGROUND_TASK);
      if (isRegistered) {
        console.log('[summaryScheduler] Unregistering existing task...');
        await BackgroundFetch.unregisterTaskAsync(BACKGROUND_TASK);
      }

      // Reset initialization flag
      this.isInitialized = false;

      // Re-initialize
      return await this.initialize();
    } catch (error) {
      const errorMessage = (error as Error).message;
      console.error('[summaryScheduler] ✗ Force re-register failed:', error);
      return { success: false, error: errorMessage };
    }
  }

  async updateAuthToken(token: string | null, userId: string | null = null): Promise<void> {
    if (!token || !userId) {
      await Promise.all([
        IS_NATIVE ? SecureStore.deleteItemAsync(STORAGE_TOKEN) : AsyncStorage.removeItem(STORAGE_TOKEN),
        AsyncStorage.removeItem(STORAGE_USER_ID),
      ]);
      return;
    }

    await Promise.all([
      IS_NATIVE ? SecureStore.setItemAsync(STORAGE_TOKEN, token) : AsyncStorage.setItem(STORAGE_TOKEN, token),
      AsyncStorage.setItem(STORAGE_USER_ID, userId),
    ]);
  }

  async syncPreferences(preferences: SummaryPreferences): Promise<void> {
    if (!IS_NATIVE) {
      await AsyncStorage.setItem(STORAGE_PREFS, JSON.stringify(preferences));
      return;
    }
    await AsyncStorage.setItem(STORAGE_PREFS, JSON.stringify(preferences));
    await this.rescheduleNotifications(preferences);
  }

  /**
   * Re-applies scheduling against the CURRENT notification preferences using
   * the last-synced summary preferences. Call after the user changes any
   * notification toggle so a master-off cancels scheduled notifications
   * immediately rather than at next app launch.
   */
  async resyncScheduledNotifications(): Promise<void> {
    if (!IS_NATIVE) return;
    try {
      const raw = await AsyncStorage.getItem(STORAGE_PREFS);
      if (!raw) return;
      const preferences: SummaryPreferences = JSON.parse(raw);
      await this.rescheduleNotifications(preferences);
    } catch (error) {
      console.error('[summaryScheduler] resyncScheduledNotifications failed', error);
    }
  }

  async clear(): Promise<void> {
    await AsyncStorage.multiRemove([STORAGE_PREFS, STORAGE_USER_ID, STORAGE_NOTIFICATIONS, STORAGE_LAST_RUN]);
    if (IS_NATIVE) {
      await SecureStore.deleteItemAsync(STORAGE_TOKEN);
    } else {
      await AsyncStorage.removeItem(STORAGE_TOKEN);
    }
    if (IS_NATIVE) {
      const notifications = await Notifications.getAllScheduledNotificationsAsync();
      await Promise.all(
        notifications
          .filter(notification => notification.identifier.startsWith('home-summary-'))
          .map(notification => Notifications.cancelScheduledNotificationAsync(notification.identifier)),
      );

      if (await TaskManager.isTaskRegisteredAsync(BACKGROUND_TASK)) {
        await BackgroundFetch.unregisterTaskAsync(BACKGROUND_TASK);
      }
    }
    this.isInitialized = false;
  }

  private async rescheduleNotifications(preferences: SummaryPreferences): Promise<void> {
    // Check app-wide notification preferences
    const notificationManager = NotificationPreferencesManager.getInstance();
    await notificationManager.initialize();
    const notificationPrefs = notificationManager.getPreferences();

    const existingRaw = await AsyncStorage.getItem(STORAGE_NOTIFICATIONS);
    const existing: NotificationMap = existingRaw ? JSON.parse(existingRaw) : {};

    // If app-wide notifications are disabled, cancel all scheduled notifications
    if (!notificationPrefs.notificationsEnabled) {
      for (const existingId of Object.values(existing)) {
        if (existingId) {
          await Notifications.cancelScheduledNotificationAsync(existingId);
        }
      }
      await AsyncStorage.setItem(STORAGE_NOTIFICATIONS, JSON.stringify({}));
      return;
    }

    const nextMap: NotificationMap = {};

    const configs = [
      {
        variant: ('morning-brief' as SummaryVariant),
        enabled: preferences.morningBrief.enabled && notificationPrefs.morningBriefNotificationsEnabled,
        time: preferences.morningBrief.time,
        title: 'Morning brief ready',
        body: "Open FimFam to review today's plan.",
      },
      {
        variant: ('evening-recap' as SummaryVariant),
        enabled: preferences.eveningRecap.enabled && notificationPrefs.eveningRecapNotificationsEnabled,
        time: preferences.eveningRecap.time,
        title: 'Evening recap ready',
        body: "See what was finished and what's coming tomorrow.",
      },
    ];

    for (const config of configs) {
      const { variant } = config;
      const existingId = existing[variant];
      if (existingId) {
        await Notifications.cancelScheduledNotificationAsync(existingId);
      }

      if (!config.enabled) {
        continue;
      }

      const [hour, minute] = config.time.split(':').map(segment => parseInt(segment, 10));
      const identifier = await Notifications.scheduleNotificationAsync({
        identifier: `home-summary-${variant}`,
        content: {
          title: config.title,
          body: config.body,
          sound: 'default',
          data: { variant },
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.DAILY,
          hour: Number.isFinite(hour) ? hour : 9,
          minute: Number.isFinite(minute) ? minute : 0,
        },
      });

      nextMap[variant] = identifier;
    }

    await AsyncStorage.setItem(STORAGE_NOTIFICATIONS, JSON.stringify(nextMap));
  }

  async runBackgroundFetch(): Promise<boolean> {
    console.log('[summaryScheduler] runBackgroundFetch called at', new Date().toISOString());

    if (!IS_NATIVE) {
      console.log('[summaryScheduler] Skipping: not on native platform');
      return false;
    }

    const preferencesRaw = await AsyncStorage.getItem(STORAGE_PREFS);
    if (!preferencesRaw) {
      console.log('[summaryScheduler] Skipping: no preferences stored');
      return false;
    }

    const preferences: SummaryPreferences = JSON.parse(preferencesRaw);
    console.log('[summaryScheduler] Loaded preferences:', {
      morningEnabled: preferences.morningBrief.enabled,
      morningTime: preferences.morningBrief.time,
      eveningEnabled: preferences.eveningRecap.enabled,
      eveningTime: preferences.eveningRecap.time,
      timezone: preferences.timezone,
    });

    const now = new Date();
    const timezone = preferences.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
    const windows = this.computeTriggerWindows(preferences, timezone, now);

    console.log('[summaryScheduler] Computed trigger windows:', windows);

    if (!windows.length) {
      console.log('[summaryScheduler] Skipping: no enabled summaries');
      return false;
    }

    const lastRunRaw = await AsyncStorage.getItem(STORAGE_LAST_RUN);
    const lastRun: LastRunMap = lastRunRaw ? JSON.parse(lastRunRaw) : {};
    console.log('[summaryScheduler] Last run dates:', lastRun);

    const token = IS_NATIVE
      ? await SecureStore.getItemAsync(STORAGE_TOKEN)
      : await AsyncStorage.getItem(STORAGE_TOKEN);
    if (!token) {
      console.log('[summaryScheduler] Skipping: no auth token');
      return false;
    }

    let didRun = false;
    const nextLastRun = { ...lastRun };

    for (const window of windows) {
      const alreadyRan = lastRun[window.variant] === window.targetDate;
      if (alreadyRan) {
        console.log(`[summaryScheduler] Skipping ${window.variant}: already ran for ${window.targetDate}`);
        continue;
      }

      const diffMinutes = (now.getTime() - window.triggerAt.getTime()) / 60000;
      console.log(`[summaryScheduler] ${window.variant}: ${diffMinutes.toFixed(1)} minutes from trigger time`);

      // Simple window: generate if we're within 0-15 minutes after trigger time
      if (diffMinutes < 0 || diffMinutes > 15) {
        console.log(`[summaryScheduler] Skipping ${window.variant}: not in 15-minute window after trigger`);
        continue;
      }

      console.log(`[summaryScheduler] Triggering generation for ${window.variant}`);
      const success = await this.triggerSummaryGeneration(window.variant, window.targetDate, token);

      if (success) {
        console.log(`[summaryScheduler] ✓ Successfully generated ${window.variant}`);
        didRun = true;
        nextLastRun[window.variant] = window.targetDate;
      } else {
        console.log(`[summaryScheduler] ✗ Failed to generate ${window.variant}`);
      }
    }

    if (didRun) {
      await AsyncStorage.setItem(STORAGE_LAST_RUN, JSON.stringify(nextLastRun));
      console.log('[summaryScheduler] Updated last run dates:', nextLastRun);
    } else {
      console.log('[summaryScheduler] No summaries generated this run');
    }

    return didRun;
  }

  private computeTriggerWindows(
    preferences: SummaryPreferences,
    timezone: string,
    now: Date,
  ): BackgroundWindow[] {
    const windows: BackgroundWindow[] = [];

    const addWindow = (variant: SummaryVariant, time: string) => {
      const target = this.buildDateForTime(time, timezone, now);
      const targetDate = this.formatDate(target, timezone);
      const triggerAt = new Date(target.getTime() - 60 * 60 * 1000); // one hour prior
      windows.push({ variant, targetDate, triggerAt });
    };

    if (preferences.morningBrief.enabled) {
      addWindow('morning-brief', preferences.morningBrief.time);
    }

    if (preferences.eveningRecap.enabled) {
      addWindow('evening-recap', preferences.eveningRecap.time);
    }

    return windows;
  }

  private buildDateForTime(time: string, timezone: string, reference: Date): Date {
    const target = new Date(reference);
    const [hours, minutes] = time.split(':').map(segment => parseInt(segment, 10));
    target.setHours(Number.isFinite(hours) ? hours : 9, Number.isFinite(minutes) ? minutes : 0, 0, 0);

    const localeString = target.toLocaleString('en-US', { timeZone: timezone });
    return new Date(localeString);
  }

  private formatDate(date: Date, timezone: string): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  private async triggerSummaryGeneration(
    variant: SummaryVariant,
    targetDate: string,
    token: string,
  ): Promise<boolean> {
    try {
      // Get userId from storage (needed for calendar sync)
      const userId = await AsyncStorage.getItem(STORAGE_USER_ID);
      if (!userId) {
        console.error('[summaryScheduler] No userId found in storage');
        return false;
      }

      // STEP 1: Sync calendars first to ensure fresh data
      console.log(`[summaryScheduler] Syncing calendars for ${variant} before generation...`);
      const syncResult = await calendarSyncService.syncForSummary(userId, variant, targetDate);

      if (!syncResult.success) {
        console.error('[summaryScheduler] Calendar sync failed:', syncResult.error);
        // Continue anyway - API will use cached events if available
        console.log('[summaryScheduler] Proceeding with summary generation using cached calendar data...');
      } else {
        console.log(`[summaryScheduler] Calendar sync successful: ${syncResult.syncedCount} events synced`);
      }

      // STEP 2: Collect family context
      let familyPayload: Record<string, unknown> = {};
      try {
        const { deviceIdentifier } = require('@/lib/storage');
        const deviceId = await deviceIdentifier.getDeviceId();
        const storage = new ScopedAsyncStorage(userId, deviceId);
        const familyContext = await FamilyContextCollector.collect(
          new LocalFamilyMembersStore(storage),
          new LocalCalendarMemberMappingStore(storage),
        );
        if (familyContext) {
          familyPayload = {
            familyMembers: familyContext.familyMembers,
            calendarMemberMappings: familyContext.calendarMemberMappings,
            meFamilyMemberId: familyContext.meFamilyMemberId,
          };
        }
      } catch (err) {
        console.warn('[summaryScheduler] Failed to collect family context:', err);
      }

      // STEP 3: Call API to generate summary
      console.log(`[summaryScheduler] Calling API to generate ${variant}...`);
      const { authenticatedFetch } = require('@/lib/services/AuthenticatedFetchService');
      const response = await authenticatedFetch.fetch('/api/home-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant, targetDate, ...familyPayload }),
      });

      if (response.ok) {
        console.log(`[summaryScheduler] ✓ Summary generation API call successful`);
        return true;
      } else {
        console.error(`[summaryScheduler] API call failed with status ${response.status}`);
        const errorText = await response.text();
        console.error('[summaryScheduler] Error response:', errorText);
        return false;
      }
    } catch (error) {
      console.error('[summaryScheduler] failed to trigger summary generation', error);
      return false;
    }
  }
}

export const summaryScheduler = new SummaryScheduler();

if (IS_NATIVE) {
  TaskManager.defineTask(BACKGROUND_TASK, async () => {
    try {
      const result = await summaryScheduler.runBackgroundFetch();
      return result
        ? BackgroundFetch.BackgroundFetchResult.NewData
        : BackgroundFetch.BackgroundFetchResult.NoData;
    } catch (error) {
      console.error('[summaryScheduler] background fetch failed', error);
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  });
}
